/**
 * Pure-function text extractors used by Coach Steve sub-agents.
 *
 * These mirror the helpers that previously lived inline in
 * `CoachSteve.tsx` and have no React/Supabase side effects, so they are
 * easy to unit-test from any agent module.
 */

import { INJURY_STATUS } from '../../../constants/injuries';
import type { InjuryStatus } from '../../../constants/injuries';

/** Common position name -> abbreviation for assign-player-to-formation parsing. */
export const POSITION_ALIASES: Record<string, string> = {
  quarterback: 'QB', qb: 'QB', 'running back': 'RB', rb: 'RB', receiver: 'WR', wr: 'WR',
  'wide receiver': 'WR', 'tight end': 'TE', te: 'TE', fullback: 'FB', fb: 'FB',
  'left tackle': 'LT', lt: 'LT', 'right tackle': 'RT', rt: 'RT', center: 'C', c: 'C',
  'left guard': 'LG', lg: 'LG', 'right guard': 'RG', rg: 'RG', slot: 'SL', sl: 'SL',
  'slot receiver': 'SL', 'halfback': 'HB', hb: 'HB', wingback: 'WB', wb: 'WB',
  'defensive end': 'RDE', 'left defensive end': 'LDE', 'right defensive end': 'RDE',
  lde: 'LDE', rde: 'RDE', 'defensive tackle': 'LDT', ldt: 'LDT', rdt: 'RDT', nt: 'NT',
  'nose tackle': 'NT', 'linebacker': 'MLB', mlb: 'MLB', wlb: 'WLB', slb: 'SLB',
  'cornerback': 'LCB', lcb: 'LCB', rcb: 'RCB', 'safety': 'FS', fs: 'FS', ss: 'SS',
  'free safety': 'FS', 'strong safety': 'SS', 'nickelback': 'NB', nb: 'NB',
};

/** Normalize position phrase: strip "the " prefix and " position" suffix, then resolve alias. */
export function normalizePositionKey(positionPart: string): string {
  const raw = positionPart.trim().replace(/^\s*the\s+/i, '').replace(/\s+position\s*$/i, '').trim();
  const posLower = raw.toLowerCase();
  return POSITION_ALIASES[posLower] || raw.toUpperCase();
}

/**
 * Parse "put John at QB in Spread" / "assign #5 to RB in Spread" / "move Ace to LDT in 4-3" style messages.
 * Returns player ref, position key, and formation name (formation required).
 */
export function extractAssignFormationFromMessage(
  text: string
): { playerRef: string; positionKey: string; formationName: string } | null {
  const trimmed = text.trim();
  const match = trimmed.match(/^(?:put|assign|set|move)\s+(.+?)\s+(?:at|to|as)\s+(.+?)\s+(?:in|for)\s+(?:the\s+)?(.+?)(?:\s+formation)?\s*$/i);
  if (!match) return null;
  const [, playerRef, positionPart, formationName] = match;
  if (!playerRef?.trim() || !positionPart?.trim() || !formationName?.trim()) return null;
  const positionKey = normalizePositionKey(positionPart);
  return {
    playerRef: playerRef.trim(),
    positionKey,
    formationName: formationName.trim(),
  };
}

/**
 * Parse "assign Ace to the LDT position" / "put Ace at LDT position" / "move Ace to LDT" (no formation).
 * Returns player ref and position key so we can ask for the formation.
 */
export function extractAssignPlayerPositionOnly(
  text: string
): { playerRef: string; positionKey: string } | null {
  const trimmed = text.trim();
  const match = trimmed.match(/^(?:assign|put|set|move)\s+(.+?)\s+(?:to|at|as)\s+(?:the\s+)?(.+?)\s*$/i);
  if (!match) return null;
  const [, playerRef, positionPart] = match;
  if (!playerRef?.trim() || !positionPart?.trim()) return null;
  const positionKey = normalizePositionKey(positionPart);
  return { playerRef: playerRef.trim(), positionKey };
}

/** Extract first/last name from an "add player" style message. */
export function extractPlayerName(text: string): { firstName?: string; lastName?: string } {
  const m =
    text.match(/\bplayer\s+([A-Za-z][A-Za-z'\-]+)\s+([A-Za-z][A-Za-z'\-]+)\b/i) ||
    text.match(/\badd\s+([A-Za-z][A-Za-z'\-]+)\s+([A-Za-z][A-Za-z'\-]+)(?:\s+#?\d+)?(?:\s+as|\s+to|\s+on|\s*[.,]|$)/i) ||
    // Require both words to start with a capital letter so pronouns/prepositions like "him in" never match.
    text.match(/\bput\s+([A-Z][a-z'\-]+)\s+([A-Z][a-z'\-]+)\b/);
  if (!m) return {};
  return { firstName: m[1], lastName: m[2] };
}

/** Extract jersey number from "#5" or "number 5" style patterns. */
export function extractJerseyNumber(text: string): number | undefined {
  const m = text.match(/(?:#|number\s+)(\d{1,3})\b/i);
  if (!m) return undefined;
  const n = parseInt(m[1], 10);
  if (Number.isNaN(n)) return undefined;
  return n;
}

/** True when the player should be flagged as a sub. */
export function extractIsSub(text: string): boolean {
  const lower = text.toLowerCase();
  if (/\bnot\s+a\s+sub\b/.test(lower) || /\bno\s+sub\b/.test(lower)) return false;
  if (/\bsub\b/.test(lower) || /\bsubstitute\b/.test(lower) || /\bfill[-\s]?in\b/.test(lower)) return true;
  return false;
}

/** Extract injury status (H/Q/D/O) from message text. Accepts synonyms (sick, injured, hurt = out). */
export function extractInjuryStatusFromText(text: string): InjuryStatus | undefined {
  const lower = text.toLowerCase();
  if (/\bout\b/.test(lower)) return INJURY_STATUS.OUT;
  if (/\bsick\b/.test(lower) || /\binjured\b/.test(lower) || /\bhurt\b/.test(lower) || /\bunavailable\b/.test(lower)) return INJURY_STATUS.OUT;
  if (/\bmarked\s+out\b/.test(lower) || /\bmark\s+out\b/.test(lower)) return INJURY_STATUS.OUT;
  if (/\bquestionable\b/.test(lower) || /\bq\b/.test(lower)) return INJURY_STATUS.QUESTIONABLE;
  if (/\bdoubtful\b/.test(lower) || /\bd\b/.test(lower)) return INJURY_STATUS.DOUBTFUL;
  if (/\bhealthy\b/.test(lower) || /\bh\b/.test(lower)) return INJURY_STATUS.HEALTHY;
  if (/\bactivate\b/.test(lower) || /\bavailable\b/.test(lower) || /\bcleared\b/.test(lower)) return INJURY_STATUS.HEALTHY;
  return undefined;
}

/**
 * Parse natural-language game details (e.g. "this coming saturday against
 * the Bolingbrook Rampage at 1pm") into form initial values.
 */
export function parseGameDetailsFromMessage(text: string): {
  date?: string;
  opponent_name?: string;
  game_time?: string;
} {
  const result: { date?: string; opponent_name?: string; game_time?: string } = {};
  const lower = text.toLowerCase().trim();

  // Opponent: "against the Bolingbrook Rampage", "vs Bolingbrook Rampage"
  const againstMatch = text.match(/(?:against|vs\.?)\s+(?:the\s+)?([A-Za-z0-9\s\-']+?)(?=\s+at\s+\d|\s+on\s+|\s*$|,|\.)/i);
  if (againstMatch && againstMatch[1].trim()) {
    result.opponent_name = againstMatch[1].trim();
  }

  // Time: "at 1pm", "1pm", "at 10:30 am", "10:30am"
  const timeMatch = lower.match(/(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/);
  if (timeMatch) {
    let h = parseInt(timeMatch[1], 10);
    const m = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
    const ampm = timeMatch[3];
    if (ampm === 'pm' && h < 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
    result.game_time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // Date: "this coming saturday", "next saturday", "saturday", "this saturday"
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayIndex = dayNames.findIndex((d) => lower.includes(d));
  if (dayIndex !== -1) {
    const today = new Date();
    const todayDay = today.getDay();
    let daysAhead = dayIndex - todayDay;
    if (daysAhead <= 0 || /\bnext\s+/.test(lower)) daysAhead += 7;
    const target = new Date(today);
    target.setDate(today.getDate() + daysAhead);
    result.date = target.toISOString().split('T')[0];
  }

  return result;
}

/**
 * Extract focus/topics from a practice request so we don't ask again.
 *   "create a practice focused on tackling" -> "tackling"
 *   "practice for base running and bunting" -> "base running and bunting"
 */
export function extractPracticeFocusFromMessage(text: string): string | null {
  const lower = text.toLowerCase().trim();
  const patterns = [
    /(?:focus(?:ed|ing)?\s+on|about|around)\s+(.+)/i,
    /(?:working\s+on|work\s+on)\s+(.+)/i,
    /(?:practice|plan)\s+(?:for|on)\s+(.+)/i,
    /(?:want|need)\s+(?:a\s+)?practice\s+(?:for|on|about)?\s*(.+)/i,
    /create\s+(?:a\s+)?practice\s+(?:plan\s+)?(?:for|on|about)?\s*(.+)/i,
    /(?:for|on)\s+(.+?)(?:\s+practice|$)/i,
  ];
  for (const re of patterns) {
    const m = lower.match(re);
    if (m && m[1]) {
      const focus = m[1].replace(/\s+practice\s*$/i, '').trim();
      if (focus.length >= 2 && !/^(plan|it|one|that|this)$/.test(focus)) return focus;
    }
  }
  // If the message is more than a short request, treat the rest as focus
  // (e.g. "create a practice - tackling and red zone")
  const createMatch = lower.match(/create\s+(?:a\s+)?practice\s*(?:plan)?\s*[\.\-—,:]\s*(.+)/);
  if (createMatch && createMatch[1].trim().length >= 2) return createMatch[1].trim();
  const practiceMatch = lower.match(/practice\s*(?:plan)?\s*[\.\-—,:]\s*(.+)/);
  if (practiceMatch && practiceMatch[1].trim().length >= 2) return practiceMatch[1].trim();
  return null;
}

/** YYYY-MM-DD for tomorrow in local time. */
export function getTomorrowDateStr(): string {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/** YYYY-MM-DD for today in local time. */
export function getTodayDateStr(): string {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/**
 * Resolve a practice date from common phrases (tomorrow, today, weekday, ISO).
 */
export function parsePracticeTargetDateFromMessage(text: string): string | null {
  const lower = text.toLowerCase();
  if (/\btomorrow\b|\btomorrows\b|\btomorrow's\b|\btomorrows'\b/i.test(lower)) return getTomorrowDateStr();
  if (/\btoday\b/.test(lower) || /\btonight\b/.test(lower)) return getTodayDateStr();
  const weekdayParsed = parseGameDetailsFromMessage(text);
  if (weekdayParsed.date) return weekdayParsed.date;
  const iso = text.match(/\b(20\d{2}-\d{2}-\d{2})\b/);
  if (iso) return iso[1];
  return null;
}

/**
 * Extract the descriptive part of an "add … drill" request.
 * e.g. "add a first baseman pickoff drill to practice tomorrow" → "first baseman pickoff"
 */
export function extractRequestedDrillPhrase(text: string): string | null {
  const patterns = [
    /\badd\s+(?:a\s+|an\s+|the\s+|another\s+)?(.+?)\s+drill(?:es|s)?\b/i,
    /\b(?:can\s+you|could\s+you)\s+add\s+(?:a\s+|an\s+|the\s+)?(.+?)\s+drill(?:es|s)?\b/i,
    /\b(?:i\s+)?want\s+(?:you\s+)?to\s+add\s+(?:a\s+|an\s+|the\s+)?(.+?)\s+drill(?:es|s)?\b/i,
    /\binclude\s+(?:a\s+|an\s+|the\s+)?(.+?)\s+drill(?:es|s)?\b/i,
    /\bput\s+(?:a\s+|an\s+|the\s+)?(.+?)\s+drill(?:es|s)?\b/i,
    /\b(?:throw|slip|work)\s+in\s+(?:a\s+|an\s+|the\s+)?(.+?)\s+drill(?:es|s)?\b/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m?.[1]) {
      let phrase = m[1].replace(/\s+/g, ' ').trim();
      phrase = phrase.replace(/^(that|this)\s+/i, '').trim();
      if (phrase.length >= 2) return phrase;
    }
  }
  return null;
}

function trimLeadingArticle(s: string): string {
  return s.replace(/^(?:a|an|the|some)\s+/i, '').trim();
}

/**
 * Drill name or informal practice segment (team game, end-of-practice finishers, etc.).
 */
export function extractPracticeActivityBrief(text: string): string | null {
  const drill = extractRequestedDrillPhrase(text);
  if (drill) return drill;

  const ofPractice =
    /\b(?:can\s+you\s+|could\s+you\s+|please\s+|(?:i\s+)?want\s+(?:you\s+)?to\s+)?add\s+(?:a\s+|an\s+|the\s+|some\s+)?(.+?)\s+of\s+practice\b/i.exec(
      text
    );
  if (ofPractice?.[1]?.trim() && trimLeadingArticle(ofPractice[1]).length >= 2) {
    return trimLeadingArticle(ofPractice[1]);
  }

  const forLastSegment =
    /\badd\s+(?:a\s+|an\s+|the\s+|some\s+)?(.+?)\s+(?:for|during)\s+(?:the\s+|tomorrow'?s\s+|tomorrows\s+)?(?:last|final)\s+\d+/i.exec(
      text
    );
  if (forLastSegment?.[1]?.trim() && trimLeadingArticle(forLastSegment[1]).length >= 2) {
    return trimLeadingArticle(forLastSegment[1]);
  }

  return null;
}

export function isAffirmativeReply(text: string): boolean {
  const t = text.trim().toLowerCase();
  return (
    /^(y|yes|yeah|yep|sure|ok|okay|please\b|please do\b|go ahead|sounds good|do it|create it|that's fine|that's ok)\b/i.test(
      t
    ) || /^absolutely\b/i.test(t)
  );
}

export function isNegativeReply(text: string): boolean {
  const t = text.trim().toLowerCase();
  return /^(n|no|nope|nah|don'?t|cancel|never mind|forget it)\b/i.test(t);
}
